/**
 * Calendar - Um componente de calendário interativo com suporte a eventos, feriados, drag & drop e temas light/dark.
 *
 * Inspirado no Fluent UI, permite visualização mensal, semanal e diária, exibição de múltiplos eventos por dia,
 * popover de detalhes, feriados, horários, localização e integração com react-dnd para arrastar compromissos.
 *
 * @component
 * @example
 * // Exemplo básico de uso:
 * <Calendar
 *   value={new Date()}
 *   onChange={(date) => setSelectedDate(date)}
 *   events={[
 *     { date: new Date(2025, 0, 1), title: "Ano Novo", color: "#e53e3e", time: "00:00" },
 *     { date: new Date(2025, 3, 21), title: "Tiradentes", color: "#3182ce", location: "Brasil" }
 *   ]}
 *   locale="pt"
 *   theme="dark"
 * />
 *
 * @example
 * // Exemplo com renderização customizada de eventos:
 * <Calendar
 *   events={[
 *     { date: new Date(), title: "Reunião", color: "#0078d4", time: "14:00", location: "Sala 1" }
 *   ]}
 *   renderEvent={event => (
 *     <div>
 *       <b>{event.title}</b>
 *       <span>{event.time}</span>
 *     </div>
 *   )}
 * />
 *
 * @param {Object} props
 * @param {Date} [props.value] Data selecionada.
 * @param {(date: Date) => void} [props.onChange] Callback ao selecionar ou mover um evento.
 * @param {CalendarEvent[]} [props.events] Lista de eventos do calendário.
 * @param {"pt"|"en"} [props.locale] Idioma do calendário.
 * @param {string} [props.className] Classe CSS adicional.
 * @param {(event: CalendarEvent) => React.ReactNode} [props.renderEvent] Função para renderizar eventos customizados.
 * @param {"light"|"dark"} [props.theme] Tema visual do calendário.
 *
 * @typedef {Object} CalendarEvent
 * @property {Date} date Data do evento.
 * @property {string} title Título do evento.
 * @property {string} [time] Horário do evento (ex: "14:00").
 * @property {string} [color] Cor do evento.
 * @property {string} [location] Localização do evento.
 *
 * @returns {JSX.Element}
 */

import React, { useRef, useState, useCallback } from "react";
import clsx from "clsx";
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { z } from "zod"; // Adicione esta linha
import Button from "../Button/Button"; // ajuste o caminho se necessário
import Card from "../Card/Card";
import FabMenu from "../FabMenu/FabMenu";
import { MdAdd } from "react-icons/md";
import CalendarEventModal from "./CalendarEventModal";
import CalendarGantt from "./CalendarGantt";
import SelectField from "../SelectField/SelectField"; // ajuste o caminho se necessário
import MiniMonthCalendar from "./MiniMonthCalendar";
import ColorPicker from "../ColorPicker/ColorPicker";
import Toggle from "../Toggle/Toggle"; // Importe o novo componente Toggle
import Modal from "../Modal/Modal"; // já importado no projeto
import Accordion from "../Accordion/Accordion"; // ajuste o caminho se necessário
import { FiMoreVertical } from "react-icons/fi"; // já deve estar instalado
import Menu from "../Menu/Menu"; // ajuste o caminho se necessário

export type CalendarLocale = "pt" | "en";

export interface CalendarEvent {
  date: Date;
  title: string;
  time?: string; // Ex: "14:00"
  color?: string;
  location?: string; // Exemplo de campo local
  assignedTo?: string[]; // Adicionado para Gantt
  [key: string]: unknown;
}

export interface TaskEvent extends CalendarEvent {
  id: string;
  parentId?: string;
  children?: TaskEvent[];
  progress?: number; // 0-100
  dependencies?: string[]; // IDs das tarefas das quais depende
}

export interface CalendarProps {
  value?: Date;
  onChange?: (date: Date) => void;
  events?: CalendarEvent[];
  locale?: CalendarLocale;
  className?: string;
  renderEvent?: (event: CalendarEvent) => React.ReactNode;
  theme?: "light" | "dark"; // Adicione esta linha
}

// Novo tipo para agenda
type CalendarSource = {
  id: string;
  name: string;
  color: string;
  visible: boolean;
};

// Defina o schema Zod para CalendarEvent
const CalendarEventSchema = z.object({
  date: z.date(),
  title: z.string(),
  color: z.string().optional(),
  location: z.string().optional(), // Adicionado campo de localização
  assignedTo: z.array(z.string()).optional(), // Adicionado para Gantt
}).passthrough();

// Defina o schema Zod para CalendarProps
const CalendarPropsSchema = z.object({
  value: z.date().optional(),
  onChange: z.function().args(z.date()).returns(z.void()).optional(),
  events: z.array(CalendarEventSchema).optional(),
  locale: z.enum(["pt", "en"]).optional(),
  className: z.string().optional(),
  renderEvent: z.function().args(CalendarEventSchema).returns(z.any()).optional(),
});

const localeMap = { pt: ptBR, en: enUS };
const weekDaysMap = {
  pt: ["S", "T", "Q", "Q", "S", "S", "D"],
  en: ["S", "M", "T", "W", "T", "F", "S"],
};
const weekStartMap: Record<CalendarLocale, 0 | 1 | 2 | 3 | 4 | 5 | 6> = { pt: 1, en: 0 };

type ViewMode = "month" | "week" | "day" | "gantt";


const holidays: { date: Date; name: string }[] = [
  { date: new Date(2025, 0, 1), name: "Ano Novo" },
  { date: new Date(2025, 3, 21), name: "Tiradentes" },
  // ...adicione outros feriados...
];

// Novo hook para drag e drop de eventos
function useEventDrag(moveEvent: (event: CalendarEvent, newDate: Date) => void) {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [dragOverDay, setDragOverDay] = useState<Date | null>(null);

  // Inicia o drag
  const handleEventMouseDown = (event: CalendarEvent, e: React.MouseEvent) => {
    setDraggedEvent(event);
    document.body.style.userSelect = "none";
    if (e) e.stopPropagation();
  };

  // Move o mouse sobre um dia
  const handleDayMouseEnter = (day: Date) => {
    if (draggedEvent) setDragOverDay(day);
  };

  // Solta o evento
  const handleDayMouseUp = (day: Date) => {
    if (draggedEvent && !isSameDay(draggedEvent.date, day)) {
      moveEvent(draggedEvent, day);
    }
    setDraggedEvent(null);
    setDragOverDay(null);
    document.body.style.userSelect = "";
  };

  // Cancela drag globalmente
  React.useEffect(() => {
    const handleUp = () => {
      setDraggedEvent(null);
      setDragOverDay(null);
      document.body.style.userSelect = "";
    };
    if (draggedEvent) {
      window.addEventListener("mouseup", handleUp);
    }
    return () => window.removeEventListener("mouseup", handleUp);
  }, [draggedEvent]);

  return {
    draggedEvent,
    dragOverDay,
    handleEventMouseDown,
    handleDayMouseEnter,
    handleDayMouseUp,
  };
}


const Calendar: React.FC<CalendarProps> = (props) => {
  // Validação das props com Zod
  CalendarPropsSchema.parse(props);

  const {
    value,
    onChange,
    events = [],
    locale = "pt",
    className,
    renderEvent,
    theme = "light", // valor padrão
  } = props;

  const themeClasses =
    theme === "dark"
      ? "text-gray-100 border-gray-700"
      : "text-gray-900 border-gray-200";
  const cellTheme =
    theme === "dark"
      ? "bg-gray-800 text-gray-100 border-gray-700"
      : "bg-white text-gray-900 border-gray-200";
  const cellInactive =
    theme === "dark"
      ? "bg-gray-900 text-gray-500"
      : "bg-gray-100 text-gray-400";
  const todayRing =
    theme === "dark"
      ? "ring-2 ring-cyan-400"
      : "ring-2 ring-blue-400";
  const selectedCell =
    theme === "dark"
      ? "bg-cyan-900 border border-cyan-400 font-semibold"
      : "bg-blue-100 border border-blue-600 font-semibold";

  const [currentMonth, setCurrentMonth] = useState(() => value || new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // Novo estado para popover de eventos
  const [popoverDay, setPopoverDay] = useState<Date | null>(null);

  // Estado para modal de novo evento
  const [eventModalOpen, setEventModalOpen] = useState(false);

  // Estado para eventos (local, para exemplo)
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>(events);
  const [initialDate, setInitialDate] = useState<Date | undefined>(undefined);

  // Estado das agendas (exemplo inicial)
  const [calendars, setCalendars] = useState<CalendarSource[]>([
    { id: "1", name: "Pessoal", color: "#0078d4", visible: true },
    { id: "2", name: "Trabalho", color: "#e53e3e", visible: true },
    { id: "3", name: "Eventos", color: "#38a169", visible: false },
  ]);

  // Novo estado para modal de cor
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [colorModalCalendarId, setColorModalCalendarId] = useState<string | null>(null);

  // Estado para modal de agenda
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [newAgendaName, setNewAgendaName] = useState("");
  const [newAgendaColor, setNewAgendaColor] = useState("#0078d4");
  const [agendaError, setAgendaError] = useState<string | null>(null);

  // Adicione ao estado do Calendar:
  const [agendaMenuId, setAgendaMenuId] = useState<string | null>(null);

  // Função para alternar visibilidade
  const toggleCalendar = (id: string) => {
    setCalendars(cals =>
      cals.map(cal =>
        cal.id === id ? { ...cal, visible: !cal.visible } : cal
      )
    );
  };

  // Função para mudar cor
  const changeCalendarColor = (id: string, color: string) => {
    setCalendars(cals =>
      cals.map(cal =>
        cal.id === id ? { ...cal, color } : cal
      )
    );
  };

  // Filtra eventos das agendas visíveis e aplica cor da agenda
  const visibleCalendarIds = calendars.filter(c => c.visible).map(c => c.id);
  const localEventsWithColor = localEvents
    .filter(
      (ev): ev is CalendarEvent =>
        !!ev && typeof ev.date !== "undefined" && typeof ev.title === "string"
    )
    .filter(ev => typeof ev.calendarId === "undefined" || visibleCalendarIds.includes(ev.calendarId as string))
    .map(ev => {
      const cal = calendars.find(c => c.id === ev.calendarId);
      return cal ? { ...ev, color: cal.color } : ev;
    });

  // Função para atualizar evento (exemplo: sobrescreva conforme sua lógica)
  const moveEvent = useCallback(
    (_event: CalendarEvent, newDate: Date) => {
      if (onChange) {
        onChange(newDate);
      }
      setLocalEvents((prev) =>
        prev.map((ev) =>
          ev === _event ? { ...ev, date: newDate } : ev
        )
      );
    },
    [onChange]
  );

  // Função para adicionar evento
  const handleCreateEvent = (event: CalendarEvent) => {
    setLocalEvents((prev) => [...prev, event]);
  };

  // Função para criar nova agenda
  const handleCreateAgenda = () => {
    if (!newAgendaName.trim()) {
      setAgendaError("Nome obrigatório");
      return;
    }
    setCalendars(cals => [
      ...cals,
      {
        id: Date.now().toString(),
        name: newAgendaName,
        color: newAgendaColor,
        visible: true,
      },
    ]);
    setNewAgendaName("");
    setNewAgendaColor("#0078d4");
    setAgendaModalOpen(false);
    setAgendaError(null);
  };

  // Hook de drag manual
  const drag = useEventDrag(moveEvent);

  const weekDays = weekDaysMap[locale];
  const weekStartsOn = weekStartMap[locale];
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn });
  const endDate = endOfMonth(monthEnd);

  // Memoize funções que afetam props
  const handleDayClick = useCallback(
    (day: Date) => {
      setSelectedDay(day);
      if (onChange) onChange(day);
      setViewMode("day");
    },
    [onChange]
  );

  const getEventsForDay = useCallback(
    (day: Date) => localEventsWithColor.filter(ev => isSameDay(ev.date, day)),
    [localEventsWithColor]
  );

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Gera matriz de semanas (6 semanas x 7 dias)
  const weeks: Date[][] = [];
  let day = startDate;
  while (day <= endDate) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  // Renderização condicional baseada no modo de visualização
  let content: React.ReactNode;
  if (viewMode === "month") {
    content = (
      <table
        className="w-full h-full border-collapse calendar-table table-fixed"
        style={{ borderSpacing: "2px" }} // <-- adicione esta linha
      >
        <thead>
          <tr>
            {weekDays.map((wd, i) => (
              <th key={i} className="text-center text-gray-500 py-1">{wd}</th>
            ))}
          </tr>
        </thead>
        <tbody className="h-full">
          {weeks.map((week, wi) => (
            <tr key={wi} className="h-full">
              {week.map((dayD, di) => (
                <CalendarDayCell
                  key={di}
                  day={dayD}
                  currentMonth={currentMonth}
                  value={value}
                  today={isToday(dayD)}
                  eventsForDay={getEventsForDay(dayD)}
                  handleDayClick={handleDayClick}
                  renderEvent={renderEvent}
                  locale={locale}
                  localeMap={localeMap}
                  popoverDay={popoverDay}
                  setPopoverDay={setPopoverDay}
                  theme={theme}
                  cellTheme={cellTheme}
                  cellInactive={cellInactive}
                  todayRing={todayRing}
                  selectedCell={selectedCell}
                  // Novos props para drag
                  draggedEvent={drag.draggedEvent}
                  dragOverDay={drag.dragOverDay}
                  handleDayMouseEnter={drag.handleDayMouseEnter}
                  handleDayMouseUp={drag.handleDayMouseUp}
                  handleEventMouseDown={drag.handleEventMouseDown}
                  setEventModalOpen={setEventModalOpen}
                  setInitialDate={setInitialDate}
                  rowCount={weeks.length} // Passe o número de linhas para calcular altura
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else if (viewMode === "week") {
    // Mostra apenas a semana do dia selecionado ou da data atual
    const baseDay = selectedDay || value || new Date();
    const weekStart = startOfWeek(baseDay, { weekStartsOn });
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(addDays(weekStart, d));
    }
    // Defina o range de horas (exemplo: 0h às 23h)
    const hours = Array.from({ length: 24 }, (_, i) => i); // 0 até 23

    content = (
      <div className="flex-1 min-h-0 overflow-y-auto w-full" style={{ maxHeight: "calc(100vh - 180px)" }}>
        <table className="w-full border-collapse table-fixed">
          <thead className="sticky top-0 z-10" style={{ background: theme === "dark" ? "#1a202c" : "#fff" }}>
            <tr>
              <th className="w-12 bg-transparent border-none"></th>
              {week.map((dayD, di) => (
                <th key={di} className="text-center text-gray-500 py-1 bg-transparent">
                  <div>
                    <span className="block font-semibold">{format(dayD, "EEE", { locale: localeMap[locale] })}</span>
                    <span className="block text-xs">{format(dayD, "d/M")}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map(hour => (
              <tr key={hour} style={{ height: 48 }}>
                <td className="text-right pr-2 align-top text-xs text-gray-400 w-12">
                  {hour.toString().padStart(2, "0")}:00
                </td>
                {week.map((dayD, di) => {
                  // Eventos deste dia e hora
                  const eventsAtHour = getEventsForDay(dayD).filter(ev => {
                    if (!ev.time) return false;
                    const [h] = ev.time.split(":");
                    return parseInt(h, 10) === hour;
                  });
                  return (
                    <td
                      key={di}
                      className={clsx(
                        "border border-gray-200 align-top relative group hover:bg-blue-50 transition",
                        isToday(dayD) && "bg-blue-50",
                        isSameDay(dayD, value ?? new Date()) && "ring-2 ring-blue-400"
                      )}
                      style={{ minWidth: 100, verticalAlign: "top" }}
                      onDoubleClick={() => {
                        setInitialDate?.(new Date(dayD.setHours(hour, 0, 0, 0)));
                        setEventModalOpen?.(true);
                      }}
                    >
                      <div className="absolute left-1 top-1 right-1 flex flex-col gap-1">
                        {eventsAtHour.map((event, idx) =>
                          renderEvent
                            ? <div key={idx}>{renderEvent(event)}</div>
                            : <div
                                key={idx}
                                className="bg-blue-100 text-blue-900 rounded px-2 py-1 text-xs mb-1 shadow cursor-pointer"
                                title={event.title}
                              >
                                {event.title}
                              </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else if (viewMode === "day") {
  const baseDay = selectedDay || value || new Date();
  const hours = Array.from({ length: 24 }, (_, i) => i); // 0 até 23

  content = (
    <div className="flex-1 min-h-0 overflow-y-auto w-full" style={{ maxHeight: "calc(100vh - 180px)" }}>
      <table className="w-full border-collapse table-fixed">
        <thead className="sticky top-0 z-10" style={{ background: theme === "dark" ? "#1a202c" : "#fff" }}>
          <tr>
            <th className="w-12 bg-transparent border-none"></th>
            <th className="text-center text-gray-500 py-1 bg-transparent">
              <div>
                <span className="block font-semibold">{format(baseDay, "EEEE", { locale: localeMap[locale] })}</span>
                <span className="block text-xs">{format(baseDay, "PPP", { locale: localeMap[locale] })}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {hours.map(hour => (
            <tr key={hour} style={{ height: 48 }}>
              <td className="text-right pr-2 align-top text-xs text-gray-400 w-12">
                {hour.toString().padStart(2, "0")}:00
              </td>
              <td
                className={clsx(
                  "border border-gray-200 align-top relative group hover:bg-blue-50 transition",
                  isToday(baseDay) && "bg-blue-50",
                  isSameDay(baseDay, value ?? new Date()) && "ring-2 ring-blue-400"
                )}
                style={{ minWidth: 100, verticalAlign: "top" }}
                onDoubleClick={() => {
                  setInitialDate?.(new Date(baseDay.setHours(hour, 0, 0, 0)));
                  setEventModalOpen?.(true);
                }}
              >
                <div className="absolute left-1 top-1 right-1 flex flex-col gap-1">
                  {getEventsForDay(baseDay)
                    .filter(ev => {
                      if (!ev.time) return false;
                      const [h] = ev.time.split(":");
                      return parseInt(h, 10) === hour;
                    })
                    .map((event, idx) =>
                      renderEvent
                        ? <div key={idx}>{renderEvent(event)}</div>
                        : <div
                            key={idx}
                            className="bg-blue-100 text-blue-900 rounded px-2 py-1 text-xs mb-1 shadow cursor-pointer"
                            title={event.title}
                          >
                            {event.title}
                          </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  } else if (viewMode === "gantt") {
    const ganttStart = startOfMonth(currentMonth);
    const ganttEnd = endOfMonth(currentMonth);
    content = (
      <CalendarGantt
        tasks={localEventsWithColor as TaskEvent[]}
        ganttStart={ganttStart}
        ganttEnd={ganttEnd}
        theme={theme}
      />
    );
  }

  const viewModeOptions = [
    { value: "month", label: "Mês" },
    { value: "week", label: "Semana" },
    { value: "day", label: "Dia" },
    { value: "gantt", label: "Gantt" },
  ];

  return (
    <Card shadow="lg" outlined className="w-full h-full max-w-full flex flex-col font-grotesk" style={{ minHeight: 0, minWidth: 0 }}>
      <div className={clsx("calendar-component flex flex-col sm:flex-row flex-1 h-full", themeClasses, className, "font-grotesk")} style={{ minHeight: 0, minWidth: 0 }}>
        {/* Mini calendário à esquerda */}
        <div className="hidden sm:block sm:w-56 sm:mr-4 flex-shrink-0 font-grotesk">
          <MiniMonthCalendar
            value={selectedDay || value || new Date()}
            onChange={day => {
              setSelectedDay(day);
              setCurrentMonth(day);
              setViewMode("day");
              if (onChange) onChange(day);
            }}
            locale={locale}
            theme={theme}
          />
          {/* Lista de agendas abaixo do mini calendário */}
          <div className="mt-4 flex flex-col gap-3">
            <Accordion theme={theme} idBase="calendar-agendas">
              <Accordion.Item index={0}>
                <Accordion.Header index={0} label="Agendas">
                  Agendas
                  <Button
                    type="button"
                    className="ml-2 px-2 py-0 rounded-full bg-blue-500 text-white text-lg font-bold hover:bg-blue-600 w-7 h-7 flex items-center justify-center text-lg font-bold"
                    onClick={e => {
                      if (e) e.stopPropagation();
                      setAgendaModalOpen(true);
                    }}
                    aria-label="Nova agenda"
                    variant="contained"
                    shape="circle"
                    size="small"
                    color="#2564cf"
                  >
                    +
                  </Button>
                </Accordion.Header>
                <Accordion.Body index={0}>
                  <div className="flex flex-col gap-3">
                    {calendars.map(cal => (
                      <AgendaListItem
                        key={cal.id}
                        cal={cal}
                        theme={theme}
                        agendaMenuId={agendaMenuId}
                        setAgendaMenuId={setAgendaMenuId}
                        setColorModalCalendarId={setColorModalCalendarId}
                        setColorModalOpen={setColorModalOpen}
                        toggleCalendar={toggleCalendar}
                      />
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            {/* Modal de seleção de cor para agenda já existente */}
            {colorModalOpen && colorModalCalendarId && (
              <ColorPicker
                value={calendars.find(c => c.id === colorModalCalendarId)?.color || "#0078d4"}
                onChange={color => {
                  changeCalendarColor(colorModalCalendarId, color);
                  setColorModalOpen(false);
                }}
                theme={theme}
              />
            )}
            {/* Modal para criar nova agenda */}
            <Modal
              open={agendaModalOpen}
              onClose={() => setAgendaModalOpen(false)}
              header="Nova Agenda"
              theme={theme}
              outlined
              shadow="md"
            >
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleCreateAgenda();
                }}
                className="flex flex-col gap-4"
              >
                <label className="font-semibold">
                  Nome
                  <input
                    type="text"
                    value={newAgendaName}
                    onChange={e => setNewAgendaName(e.target.value)}
                    className="mt-1 px-2 py-1 rounded border border-gray-300 w-full"
                    autoFocus
                    required
                  />
                </label>
                <label className="font-semibold">
                  Cor
                  <ColorPicker
                    value={newAgendaColor}
                    onChange={setNewAgendaColor}
                    theme={theme}
                    showInputField={true}
                    showPreview={true}
                    className="mt-1"
                  />
                </label>
                {agendaError && <span className="text-red-500 text-sm">{agendaError}</span>}
                <div className="flex justify-end gap-2 mt-2">
                  <Button type="button" onClick={() => setAgendaModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="contained" color="primary" onClick={() => {}}>
                    Criar
                  </Button>
                </div>
              </form>
            </Modal>
          </div>
        </div>
        {/* Conteúdo principal */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 font-grotesk">
          {/* FabMenu para criar evento */}
          <FabMenu
            label="Ações"
            direction="up"
            theme={theme}
            className="fixed bottom-8 right-8 z-[1200]"
          >
            <button
              onClick={() => setEventModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2"
            >
              <MdAdd /> Novo Evento
            </button>
          </FabMenu>
          <CalendarEventModal
            open={eventModalOpen}
            onClose={() => setEventModalOpen(false)}
            onCreate={handleCreateEvent}
            initialDate={initialDate}
            theme={theme}
          />

          {/* Header novo, inspirado no Google Calendar */}
          <div className="calendar-header flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2 font-inter">
            <div className="flex items-center gap-2">
              {/* Botão Hoje */}
              <Button
                type="button"
                onClick={() => {
                  setCurrentMonth(new Date());
                  setSelectedDay(new Date());
                  setViewMode("day");
                  if (onChange) onChange(new Date());
                }}
                ariaLabel="Ir para hoje"
                variant="contained"
                shape="pill"
                className="font-inter"
                buttonClassName="rounded-full font-inter"
              >
                Hoje
              </Button>
              {/* Botão mês anterior */}
              <Button
                type="button"
                onClick={prevMonth}
                ariaLabel="Mês anterior"
                size="tiny"
                variant="text"
                shape="default"
                className={clsx(
                  "w-7 h-7 flex items-center justify-center rounded-full p-0 border-none bg-transparent hover:bg-gray-200 text-lg transition"
                )}
                buttonClassName={clsx(
                  "border-none shadow-none",
                  theme === "dark" ? "text-gray-100 hover:bg-gray-700" : "text-gray-900"
                )}
              >
                {"‹"}
              </Button>
              {/* Nome do mês */}
              <span
                className={clsx(
                  "font-semibold mx-1 text-lg select-none font-inter",
                  theme === "dark" ? "text-gray-100" : "text-gray-800"
                )}
                style={{ minWidth: 120, textAlign: "center" }}
              >
                {format(currentMonth, "MMMM yyyy", { locale: localeMap[locale] })}
              </span>
              {/* Botão mês seguinte */}
              <Button
                type="button"
                onClick={nextMonth}
                ariaLabel="Próximo mês"
                size="tiny"
                variant="text"
                shape="default"
                className={clsx(
                  "w-7 h-7 flex items-center justify-center rounded-full p-0 border-none bg-transparent hover:bg-gray-200 text-lg transition"
                )}
                buttonClassName={clsx(
                  "border-none shadow-none",
                  theme === "dark" ? "text-gray-100 hover:bg-gray-700" : "text-gray-900"
                )}
              >
                {"›"}
              </Button>
            </div>
            {/* Select de modo de visualização */}
            <div className="flex items-center gap-2 font-inter">
              <SelectField
                options={viewModeOptions}
                value={viewMode}
                onChange={v => setViewMode(v as ViewMode)}
                theme={theme}
                className="min-w-[110px] w-auto"
                inputClassName="py-1 px-2 text-sm rounded-md"
                dropdownClassName="text-sm"
                containerProps={{ style: { marginBottom: 0 } }}
                aria-label="Modo de visualização"
              />
            </div>
          </div>
          {/* Só o calendário tem rolagem */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 font-grotesk">
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto font-grotesk">
              {content}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

type CalendarDayCellProps = {
  day: Date;
  currentMonth: Date;
  value?: Date;
  today: boolean;
  eventsForDay: CalendarEvent[];
  handleDayClick: (day: Date) => void;
  renderEvent?: (event: CalendarEvent) => React.ReactNode;
  locale: CalendarLocale;
  localeMap: typeof localeMap;
  popoverDay: Date | null;
  setPopoverDay: React.Dispatch<React.SetStateAction<Date | null>>;
  theme: "light" | "dark";
  cellTheme: string;
  cellInactive: string;
  todayRing: string;
  selectedCell: string;
  // Novos props para drag
  draggedEvent: CalendarEvent | null;
  dragOverDay: Date | null;
  handleDayMouseEnter: (day: Date) => void;
  handleDayMouseUp: (day: Date) => void;
  handleEventMouseDown: (event: CalendarEvent, e: React.MouseEvent) => void;
  // Novo prop para abrir modal
  setEventModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
};

// Novo componente interno para célula de dia como div interativa
const CalendarDayInteractive: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
  ariaLabel: string;
  title: string;
}> = ({ children, onClick, className, ariaLabel, title }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={e => (e.key === "Enter" || e.key === " ") && onClick()}
    className={clsx(
      "w-full h-full rounded-md flex flex-col items-center justify-start transition border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 select-none",
      className
    )}
    aria-label={ariaLabel}
    title={title}
  >
    {children}
  </div>
);

const CalendarDayCell: React.FC<CalendarDayCellProps & { rowCount?: number }> = ({
  day,
  currentMonth,
  value,
  today,
  eventsForDay,
  renderEvent,
  locale,
  localeMap,
  popoverDay,
  setPopoverDay,
  theme,
  cellTheme,
  cellInactive,
  todayRing,
  selectedCell,
  draggedEvent,
  dragOverDay,
  handleDayMouseEnter,
  handleDayMouseUp,
  handleEventMouseDown,
  // Novo prop para abrir modal
  setEventModalOpen,
  setInitialDate,
  rowCount = 6, // padrão para mês
}) => {
  const isCurrentMonth = isSameMonth(day, currentMonth);
  const selected = value && isSameDay(day, value);
  const isDragOver = dragOverDay && isSameDay(dragOverDay, day);
  const isHoliday = holidays.some(h => isSameDay(h.date, day));
  const holidayName = holidays.find(h => isSameDay(h.date, day))?.name;

  // Novo: ao clicar, define data inicial e abre modal
  const handleOpenModal = () => {
    setInitialDate?.(day);
    setEventModalOpen?.(true);
  };

  return (
    <td
      className={clsx(
        "p-0 relative align-top h-full",
        isDragOver && "ring-2 ring-blue-400 ring-inset" // destaque visual ao arrastar
      )}
      onMouseEnter={() => handleDayMouseEnter(day)}
      onMouseUp={() => handleDayMouseUp(day)}
      style={{
        minWidth: 48,
        verticalAlign: "top",
        height: `calc(100vh / ${rowCount + 2})`,
        maxHeight: "1px",
      }}
    >
      <CalendarDayInteractive
        onClick={handleOpenModal}
        className={clsx(
          isCurrentMonth ? cellTheme : cellInactive,
          today && todayRing,
          selected && selectedCell,
          "h-full"
        )}
        ariaLabel={format(day, "PPP", { locale: localeMap[locale] })}
        title={format(day, "d")}
      >
        <div className="flex flex-col items-center w-full">
          <span
            className={clsx(
              "mt-1 mb-2 font-semibold text-base",
              theme === "dark" ? "text-gray-100" : "text-gray-800"
            )}
          >
            {format(day, "d")}
          </span>
          {isHoliday && (
            <span className="block text-xs text-red-500 font-bold mb-1" title={holidayName}>
              🎉
            </span>
          )}
        </div>
        <div className="flex flex-col items-start w-full gap-1">
          {eventsForDay.slice(0, 4).map((event, idx) => (
            <DraggableEvent
              key={idx}
              event={event}
              renderEvent={renderEvent}
              theme={theme}
              onMouseDown={e => handleEventMouseDown(event, e)}
              isDragging={draggedEvent === event}
            />
          ))}
          {eventsForDay.length > 4 && (
            <span className="text-xs text-blue-600 mt-1">+{eventsForDay.length - 4}</span>
          )}
        </div>
      </CalendarDayInteractive>
      {/* Popover de eventos do dia */}
      {popoverDay && isSameDay(popoverDay, day) && (
        <div className="absolute z-20 left-1/2 top-12 -translate-x-1/2 w-64">
          <Card
            outlined
            shadow="lg"
            className={clsx(
              theme === "dark"
                ? "bg-gray-900 border border-cyan-900 text-gray-100"
                : "bg-white border border-blue-200"
            )}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold">{format(day, "PPP", { locale: localeMap[locale] })}</span>
              <Button
                type="button"
                onClick={() => setPopoverDay(null)}
                ariaLabel="Fechar"
                className="ml-2 px-2 py-0 text-xs"
              >
                ×
              </Button>
            </div>
            {eventsForDay.length === 0 && (
              <div className="text-gray-400">Sem eventos</div>
            )}
            {eventsForDay.map((event, idx) =>
              renderEvent
                ? <div key={idx}>{renderEvent(event)}</div>
                : (
                  <Card
                    key={idx}
                    className={clsx(
                      "mb-2 p-2",
                      theme === "dark"
                        ? "bg-gray-800 text-gray-100 border-gray-700"
                        : "bg-white text-gray-900 border-gray-200"
                    )}
                    outlined
                  >
                    <div className="flex items-center">
                      <span className="block w-2 h-2 rounded-full mr-2" style={{ background: event.color || "#0078d4" }} />
                      <span>
                        {event.time && <span className="text-xs text-gray-500 mr-1">{event.time}</span>}
                        {event.title}
                      </span>
                    </div>
                    {/* Exibir localização no popover */}
                    {event.location && (
                      <div className="text-xs text-gray-400">{event.location}</div>
                    )}
                  </Card>
                )
            )}
          </Card>
        </div>
      )}
    </td>
  );
};

type DraggableEventProps = {
  event: CalendarEvent;
  renderEvent?: (event: CalendarEvent) => React.ReactNode;
  theme: "light" | "dark";
  onMouseDown: (e: React.MouseEvent) => void;
  isDragging: boolean;
};

const DraggableEvent: React.FC<DraggableEventProps> = ({ event, renderEvent, onMouseDown, isDragging }) => {
  return (
    <span
      onMouseDown={onMouseDown}
      className={clsx("cursor-pointer select-none", isDragging && "opacity-50")}
      tabIndex={0}
      aria-label="Arrastar evento"
      // role="button" // Descomente se quiser indicar que é clicável
    >
      {renderEvent ? renderEvent(event) : (
        <div className="flex items-center">
          <span className="block w-2 h-2 rounded-full mr-2" style={{ background: event.color || "#0078d4" }} />
          <span className="text-sm">{event.title}</span>
        </div>
      )}
    </span>
  );
};


// Novo componente para cada item de agenda, para uso correto de hooks
const AgendaListItem: React.FC<{
  cal: CalendarSource;
  theme: "light" | "dark";
  agendaMenuId: string | null;
  setAgendaMenuId: React.Dispatch<React.SetStateAction<string | null>>;
  setColorModalCalendarId: React.Dispatch<React.SetStateAction<string | null>>;
  setColorModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleCalendar: (id: string) => void;
}> = ({
  cal,
  theme,
  agendaMenuId,
  setAgendaMenuId,
  setColorModalCalendarId,
  setColorModalOpen,
  toggleCalendar,
}) => {
  // Troque HTMLElement por HTMLButtonElement:
  const btnRef = useRef<HTMLButtonElement>(null);

  // Menu items para agenda
  const menuItems = [
    {
      label: "Alterar cor",
      icon: (
        <span
          className="inline-block w-4 h-4 rounded-full border"
          style={{ background: cal.color, borderColor: cal.color }}
        />
      ),
      onClick: () => {
        setColorModalCalendarId(cal.id);
        setColorModalOpen(true);
      },
    },
    {
      label: cal.visible ? "Ocultar agenda" : "Mostrar agenda",
      icon: <FiMoreVertical className="opacity-60" />,
      onClick: () => toggleCalendar(cal.id),
    },
  ];

  return (
    <div className="flex items-center gap-2 relative group">
      <Toggle
        value={cal.visible}
        onValueChange={() => toggleCalendar(cal.id)}
        label={cal.name}
        theme={theme}
      />
      <span
        className="cursor-pointer underline ml-2"
        style={{ color: cal.color }}
        title="Cor da agenda"
        onClick={e => {
          e.stopPropagation();
          setColorModalCalendarId(cal.id);
          setColorModalOpen(true);
        }}
      >
        ●
      </span>
      <button
        type="button"
        ref={btnRef}
        className={clsx(
          "ml-1 p-1 rounded-full hover:bg-gray-200 transition",
          theme === "dark" ? "hover:bg-gray-700 text-gray-100" : "text-gray-700"
        )}
        onClick={e => {
          e.stopPropagation();
          setAgendaMenuId(agendaMenuId === cal.id ? null : cal.id);
        }}
        aria-label="Mais opções"
      >
        <FiMoreVertical />
      </button>
      {/* Novo Menu contextual */}
      <Menu
        open={agendaMenuId === cal.id}
        anchorEl={btnRef as React.RefObject<HTMLElement>}
        items={menuItems}
        onClose={() => setAgendaMenuId(null)}
        theme={theme}
        placement="right-start"
      />
    </div>
  );
};

export default Calendar;
